import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  useTheme,
  Button,
  Divider,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Flag as FlagIcon,
  Inventory,
  MiscellaneousServices,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { serviceProductService } from '@/services/business/serviceProductService';
import { getFileUrl } from '@/utils/businessHelpers';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

interface ProductReport {
  _id: string;
  serviceProductId: any;
  reportedBy: { _id: string; name?: string; email?: string };
  reason: string;
  createdAt: string;
  updatedAt: string;
}

const ServiceProductReportsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [reports, setReports] = useState<ProductReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchReports = async (pageNum: number, append = false) => {
    setIsLoading(true);
    try {
      const response: any = await serviceProductService.getServiceProductReports(pageNum, 10);
      if (response?.success) {
        const fetched: ProductReport[] = response.data?.reports || [];
        setReports(prev => (append ? [...prev, ...fetched] : fetched));
        setHasMore(Boolean(response.data?.pagination?.hasNext));
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to fetch service/product reports');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(1);
  }, []);

  const handleRefresh = () => {
    setPage(1);
    fetchReports(1);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchReports(nextPage, true);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Reported Services / Products
        </Typography>
        <IconButton onClick={handleRefresh} disabled={isLoading} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {reports.length === 0 && !isLoading ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="body1" color="text.secondary">
            No service/product reports to review at this time.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {reports.map((report) => {
            const product = report.serviceProductId;
            const businessInfo = product?.businessId && typeof product.businessId === 'object' ? product.businessId : null;
            const productBusinessId = typeof product?.businessId === 'string' ? product?.businessId : product?.businessId?._id;
            return (
              <Box key={report._id} sx={{ mb: 4 }}>
                {/* Report meta */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 1,
                    bgcolor: theme.palette.mode === 'light' ? '#fff4f4' : '#2d1a1a',
                    border: `1px solid ${theme.palette.error.light}`,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <FlagIcon color="error" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="bold" color="error">
                      Reported by {report.reportedBy?.name || 'Unknown'} ({report.reportedBy?.email})
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Reason: <strong>{report.reason}</strong> • {moment(report.createdAt).fromNow()}
                    </Typography>
                  </Box>
                </Paper>

                {/* Product card */}
                {product ? (
                  <Paper
                    elevation={0}
                    onClick={() => {
                      if (productBusinessId) {
                        navigate(`/businesses/${productBusinessId}/serviceProducts/${product._id}`);
                      }
                    }}
                    sx={{
                      p: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      cursor: 'pointer',
                      display: 'flex',
                      gap: 2,
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: theme.shadows[3] },
                    }}
                  >
                    {/* Image */}
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        flexShrink: 0,
                        borderRadius: 2,
                        overflow: 'hidden',
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {product.productImage?.url ? (
                        <Box component="img" src={product.productImage.url} alt={product.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : product.serviceType === 'product' ? (
                        <Inventory sx={{ fontSize: 40, color: 'text.disabled' }} />
                      ) : (
                        <MiscellaneousServices sx={{ fontSize: 40, color: 'text.disabled' }} />
                      )}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={product.serviceType === 'product' ? 'Product' : 'Service'}
                          size="small"
                          color={product.serviceType === 'product' ? 'primary' : 'secondary'}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                        {product.isDeleted && <Chip label="Deleted" size="small" color="error" sx={{ fontSize: '0.7rem' }} />}
                        {!product.isActive && <Chip label="Inactive" size="small" sx={{ fontSize: '0.7rem' }} />}
                      </Box>

                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {product.title}
                      </Typography>

                      <Typography variant="h6" color="primary" fontWeight={700} sx={{ my: 0.25 }}>
                        {product.price !== undefined && product.price > 0
                          ? `₹${Number(product.price).toLocaleString('en-IN')}`
                          : 'Price on Request'}
                      </Typography>

                      {product.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mb: 1,
                          }}
                        >
                          {product.description}
                        </Typography>
                      )}

                      {businessInfo && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, pt: 1, borderTop: `1px dashed ${theme.palette.divider}` }}>
                          <Avatar
                            src={businessInfo.logoId ? getFileUrl(businessInfo.logoId) : undefined}
                            sx={{ width: 24, height: 24, fontSize: 12 }}
                          >
                            {businessInfo.businessName?.charAt(0) || <BusinessIcon sx={{ fontSize: 14 }} />}
                          </Avatar>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {businessInfo.businessName}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                ) : (
                  <Paper sx={{ p: 2, textAlign: 'center', border: `1px dashed ${theme.palette.divider}` }}>
                    <Typography variant="body2" color="text.secondary">
                      Reported service/product has been deleted or is unavailable.
                    </Typography>
                  </Paper>
                )}
                <Divider sx={{ mt: 2 }} />
              </Box>
            );
          })}

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={isLoading}
                sx={{ borderRadius: '20px', px: 4 }}
              >
                {isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : 'Load More Reports'}
              </Button>
            </Box>
          )}

          {!hasMore && reports.length > 0 && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
              You've reviewed all service/product reports.
            </Typography>
          )}
        </Box>
      )}

      {isLoading && reports.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default ServiceProductReportsPage;
